/**
 * BiquadFilterNode - Web Audio biquad filter
 *
 * Endpoints:
 * - audioIn (input): audio to filter
 * - audioOut (output): filtered audio
 * - type: filter type (lowpass, highpass, bandpass, etc.)
 * - frequency: cutoff/center frequency in Hz
 * - Q: quality factor
 *
 * Metadata passthrough is automatic via AudioBaseNode.
 */

import { AudioBaseNode, type AudioDependencies } from '../AudioBaseNode';
import { Domain } from '@nodes/Endpoint';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@graph/Payload';
import { audio, params } from '@nodes/TypedEndpoints';

const endpoints = {
  ...audio({
    audioIn: { isInput: true },
    audioOut: { isOutput: true }
  }),
  ...params({
    type: {
      dataType: 'string',
      value: 'lowpass',
      options: ['lowpass', 'highpass', 'bandpass', 'notch', 'allpass', 'peaking', 'lowshelf', 'highshelf'] as const
    },
    frequency: { value: 1000, min: 20, max: 20000, step: 1 },
    Q: { label: 'Q', value: 1, min: 0.1, max: 30, step: 0.1 },
  })
};

export class BiquadFilterNode extends AudioBaseNode<typeof endpoints> {
  private filterNode: any = null;

  constructor(id: string, deps: AudioDependencies) {
    super(id, deps);
  }

  initialize(): void {
    super.initialize();

    this.filterNode = this.audioContext.createBiquadFilter();
    this.filterNode.type = this.params.type.value as BiquadFilterType;
    this.filterNode.frequency.value = this.params.frequency.value;
    this.filterNode.Q.value = this.params.Q.value;

    // Chain: audioIn → filter → audioOut
    this.audio.audioIn.connect(this.filterNode);
    this.audio.audioOut.connect(this.filterNode);
  }

  onParamChanged(payload: Payload, endpointId: string): void {
    if (!this.filterNode) return;
    switch (endpointId) {
      case 'type':
        this.filterNode.type = payload.value as BiquadFilterType;
        break;
      case 'frequency':
        this.filterNode.frequency.value = payload.value;
        break;
      case 'Q':
        this.filterNode.Q.value = payload.value;
        break;
    }
  }

  dispose(): void {
    if (this.filterNode) {
      this.filterNode.disconnect();
      this.filterNode = null;
    }
    super.dispose();
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const biquadFilterNodeDef: NodeDef<BiquadFilterNode> = {
  type: 'BiquadFilter',
  shortName: 'filter',
  info: 'Web Audio biquad filter. Supports lowpass, highpass, bandpass, notch, and shelf modes.',
  domain: Domain.Audio,
  category: 'effect',
  endpoints,
  ctor: BiquadFilterNode
};
